import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { FaSave, FaFilePdf, FaEye, FaUsers, FaQrcode, FaTimes, FaCamera, FaSearch, FaCheckCircle } from 'react-icons/fa';
import { Html5Qrcode } from 'html5-qrcode';
import { hasEventStarted, getEventStartDateTime, formatIST } from '../../utils/eventTiming';
import Spinner from '../common/Spinner';

// ✅ How often to re-pull attendance from the server while this screen is
// open, so that when multiple staff mark attendance for the same event in
// parallel, each one's screen picks up the others' updates without a manual
// refresh.
const LIVE_SYNC_INTERVAL_MS = 5000;

const MarkAttendance = ({ event, onComplete, user }) => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showStudentsList, setShowStudentsList] = useState(false);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
    const [showStudentDetails, setShowStudentDetails] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [manualSearchTerm, setManualSearchTerm] = useState('');
    
    // ✅ Refs to avoid DOM issues
    const scannerRef = useRef(null);
    const containerId = 'barcode-scanner-container';
    const isInitialized = useRef(false);
    const lastScanned = useRef('');
    // Bumped on every local mark, so a background sync that was already in
    // flight (carrying data from before that mark) doesn't briefly undo it.
    const localChangeSeq = useRef(0);

    useEffect(() => {
        fetchAttendance();
        // ✅ Poll for updates so that when multiple staff mark attendance for
        // this same event in parallel, everyone's screen picks up everyone
        // else's changes without needing a manual refresh.
        const syncInterval = setInterval(() => fetchAttendance({ silent: true }), LIVE_SYNC_INTERVAL_MS);
        return () => {
            clearInterval(syncInterval);
            cleanupScanner();
        };
    }, []);

    const fetchAttendance = async ({ silent = false } = {}) => {
        try {
            const seqAtStart = localChangeSeq.current;
            const response = await api.get(`/attendance/${event._id}`);
            if (silent && seqAtStart !== localChangeSeq.current) return; // stale - next sync will catch up
            const attendance = response.data.attendance || [];
            const updatedAttendance = attendance.map(a => ({
                ...a,
                status: a.status || 'absent'
            }));
            setAttendanceData(updatedAttendance);
            return true;
        } catch (error) {
            if (!silent) toast.error('Failed to load attendance data');
            return false;
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // ✅ Only the event's primary staff (creator) or an admin may finalize
    // and close attendance - secondary coordinators can mark attendance
    // alongside them, but can't be the one to sign off and close it out.
    const isPrimaryStaff = user?.role === 'admin' || event.createdBy?._id === user?._id;

    const markPresent = async (studentId) => {
        try {
            await api.post(`/attendance/${event._id}/mark`, { studentId, status: 'present' });
            localChangeSeq.current += 1;
            setAttendanceData(prev => prev.map(a => a.student._id === studentId ? { ...a, status: 'present' } : a));
            return true;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to mark attendance');
            return false;
        }
    };

    const markAbsent = async (studentId) => {
        try {
            await api.post(`/attendance/${event._id}/mark`, { studentId, status: 'absent' });
            localChangeSeq.current += 1;
            setAttendanceData(prev => prev.map(a => a.student._id === studentId ? { ...a, status: 'absent' } : a));
            toast.success('✅ Marked absent');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to mark attendance');
        }
    };

    // ✅ Cleanup scanner
    const cleanupScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                await scannerRef.current.clear();
            } catch (e) {
                // Ignore cleanup errors
            }
            scannerRef.current = null;
        }
        isInitialized.current = false;
    };

    // ✅ START SCANNER
    const startScanner = async () => {
        try {
            // Cleanup existing scanner
            await cleanupScanner();

            setScanResult(null);
            setBarcodeInput('');
            setIsClosing(false);
            lastScanned.current = '';

            // Check camera support
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                toast.error('Camera not supported on this device');
                return;
            }

            // Show scanner modal FIRST
            setShowScanner(true);

            // Wait for DOM to render
            setTimeout(async () => {
                try {
                    const container = document.getElementById(containerId);
                    if (!container) {
                        toast.error('Scanner container not found');
                        setShowScanner(false);
                        return;
                    }

                    // ✅ Create scanner instance
                    const html5QrCode = new Html5Qrcode(containerId);
                    scannerRef.current = html5QrCode;
                    isInitialized.current = true;

                    const config = {
                        fps: 10,
                        qrbox: { width: 250, height: 200 },
                        aspectRatio: 1.0
                    };

                    // ✅ Start scanning
                    await html5QrCode.start(
                        { facingMode: "environment" },
                        config,
                        (decodedText) => {
                            console.log('📱 Scanned:', decodedText);
                            
                            // ✅ Auto-fill text box
                            setBarcodeInput(decodedText);
                            
                            // ✅ Auto-process if not already processing and not duplicate
                            if (!isProcessing && decodedText !== lastScanned.current) {
                                lastScanned.current = decodedText;
                                handleBarcodeScan(decodedText);
                            }
                        },
                        (error) => {
                            // Only log serious errors
                            if (error && !error.includes('No QR code') && !error.includes('No MultiFormat')) {
                                console.log('Scan error:', error);
                            }
                        }
                    );

                    toast.success('📷 Scanner ready! Scan student barcodes.');

                } catch (error) {
                    console.error('Scanner init error:', error);
                    toast.error('Failed to start camera. Please check permissions.');
                    setShowScanner(false);
                    isInitialized.current = false;
                }
            }, 500);

        } catch (error) {
            console.error('Camera error:', error);
            toast.error('Failed to start scanner');
            setShowScanner(false);
        }
    };

    // ✅ STOP SCANNER - Only when staff clicks Close
    const stopScanner = async () => {
        if (isClosing) return;
        setIsClosing(true);

        await cleanupScanner();

        setShowScanner(false);
        setBarcodeInput('');
        setScanResult(null);
        setIsClosing(false);
        isInitialized.current = false;
        lastScanned.current = '';
    };

    // ✅ HANDLE BARCODE SCAN - Marks present, stays open
    const handleBarcodeScan = async (barcodeData) => {
        if (isProcessing) return;
        setIsProcessing(true);
        setScanResult(null);

        // ✅ Find student by registration number (case insensitive)
        const matchedStudent = attendanceData.find(a => 
            a.student.regNo && 
            barcodeData.toLowerCase().trim() === a.student.regNo.toLowerCase().trim()
        );

        if (matchedStudent) {
            // ✅ Already present
            if (matchedStudent.status === 'present') {
                setScanResult({
                    success: true,
                    student: matchedStudent.student,
                    message: `✅ ${matchedStudent.student.name} (${matchedStudent.student.regNo}) already present`
                });
                setIsProcessing(false);
                setBarcodeInput('');
                return;
            }

            // ✅ Mark present
            const success = await markPresent(matchedStudent.student._id);
            if (success) {
                setScanResult({
                    success: true,
                    student: matchedStudent.student,
                    message: `✅ ${matchedStudent.student.name} (${matchedStudent.student.regNo}) marked present!`
                });
                setBarcodeInput('');
                // ✅ SCANNER STAYS OPEN - Ready for next student
            } else {
                setScanResult({
                    success: false,
                    message: `❌ Failed to mark ${matchedStudent.student.name} present.`
                });
            }
        } else {
            // ✅ Fuzzy match by name or email
            const fuzzyMatch = attendanceData.find(a => {
                const student = a.student;
                return (
                    (student.name && barcodeData.toLowerCase().includes(student.name.toLowerCase())) ||
                    (student.email && barcodeData.toLowerCase().includes(student.email.toLowerCase())) ||
                    (student._id && barcodeData.includes(student._id))
                );
            });

            if (fuzzyMatch) {
                if (fuzzyMatch.status === 'present') {
                    setScanResult({
                        success: true,
                        student: fuzzyMatch.student,
                        message: `✅ ${fuzzyMatch.student.name} (${fuzzyMatch.student.regNo}) already present`
                    });
                } else {
                    const success = await markPresent(fuzzyMatch.student._id);
                    if (success) {
                        setScanResult({
                            success: true,
                            student: fuzzyMatch.student,
                            message: `✅ ${fuzzyMatch.student.name} (${fuzzyMatch.student.regNo}) marked present!`
                        });
                        setBarcodeInput('');
                    } else {
                        setScanResult({
                            success: false,
                            message: `❌ Failed to mark ${fuzzyMatch.student.name} present.`
                        });
                    }
                }
            } else {
                // ✅ No match found
                setScanResult({
                    success: false,
                    message: `❌ Registration number "${barcodeData}" does not match any student in this event.`
                });
            }
        }
        setIsProcessing(false);
    };

    // ✅ Manual submit (fallback)
    const handleManualSubmit = () => {
        if (!barcodeInput.trim()) {
            toast.error('Please enter a registration number');
            return;
        }
        handleBarcodeScan(barcodeInput.trim());
    };

    const filteredManualStudents = manualSearchTerm.trim()
        ? attendanceData.filter(a =>
            a.student.name?.toLowerCase().includes(manualSearchTerm.toLowerCase()) ||
            a.student.regNo?.toLowerCase().includes(manualSearchTerm.toLowerCase())
          )
        : [];

    const handleManualMarkPresent = async (studentId, studentName) => {
        const success = await markPresent(studentId);
        if (success) {
            toast.success(`✅ ${studentName} marked present!`);
        }
    };

    const handleViewStudentDetails = (student) => {
        setSelectedStudentDetails(student);
        setShowStudentDetails(true);
    };

    // Every Present/Absent is saved to the server the moment it's marked, so
    // there's nothing local left to save. This used to re-send this screen's
    // whole table, which could overwrite marks other staff had just made in
    // parallel - now it only confirms and pulls in everyone's latest marks.
    const saveProgress = async () => {
        setSaving(true);
        try {
            if (await fetchAttendance()) {
                toast.success('💾 All marks are saved - showing the latest from all staff');
            }
        } finally {
            setSaving(false);
        }
    };

    const finalizeAttendance = async () => {
        if (!isPrimaryStaff) {
            toast.error('Only the primary staff (event creator) can finalize and close attendance');
            return;
        }
        if (!window.confirm('⚠️ Finalize attendance? This will send reports and notifications.')) return;
        setSaving(true);
        try {
            const response = await api.post(`/attendance/${event._id}/finalize`);
            toast.success(`✅ Attendance finalized! Present: ${response.data.stats.present}, Absent: ${response.data.stats.absent}`);
            if (response.data.warnings?.length > 0) {
                toast(`⚠️ ${response.data.warnings.length} issue(s) occurred - check with admin`, { icon: '⚠️' });
                console.warn('Finalize warnings:', response.data.warnings);
            }
            if (onComplete) onComplete();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to finalize attendance');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '30px' }}>Loading attendance...</div>;

    if (!hasEventStarted(event)) {
        return (
            <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <h3 className="card-title">📋 Mark Attendance - {event.name}</h3>
                <p style={{ fontSize: '15px', color: '#666', marginTop: '15px' }}>
                    ⏳ This event hasn't started yet. Attendance can only be marked once it begins.
                </p>
                <p style={{ fontSize: '14px', color: '#0a1e3c', fontWeight: 600, marginTop: '5px' }}>
                    Starts: {formatIST(getEventStartDateTime(event))} IST
                </p>
                <button className="btn btn-primary" style={{ marginTop: '20px' }} onClick={onComplete}>
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h3 className="card-title">📋 Mark Attendance - {event.name}</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {/* ✅ ONLY ONE BARCODE SCANNER BUTTON - Common at top */}
                    <button 
                        className="btn btn-primary" 
                        onClick={startScanner} 
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        disabled={showScanner}
                    >
                        <FaCamera /> {showScanner ? 'Scanner Active' : 'Barcode Scanner'}
                    </button>
                    <button
                        className="btn btn-primary"
                        style={{ background: '#6c757d' }}
                        onClick={() => { setManualSearchTerm(''); setShowManualEntry(true); }}
                    >
                        <FaSearch /> Manual Entry
                    </button>
                    <button className="btn btn-info" onClick={() => setShowStudentsList(true)}>
                        <FaUsers /> View Students
                    </button>
                    <button className="btn btn-warning" onClick={saveProgress} disabled={saving}>
                        {saving ? <Spinner /> : <FaSave />} {saving ? 'Saving...' : 'Save Progress'}
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={finalizeAttendance}
                        disabled={saving || !isPrimaryStaff}
                        title={isPrimaryStaff ? '' : 'Only the primary staff (event creator) can finalize and close attendance'}
                    >
                        {saving ? <Spinner /> : <FaFilePdf />} {saving ? 'Processing...' : 'Finalize & Close'}
                    </button>
                </div>
            </div>

            {/* ✅ SCANNER MODAL - Persistent, stays open for multiple scans */}
            {showScanner && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '25px',
                        maxWidth: '500px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        position: 'relative'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ color: '#0a1e3c', margin: 0 }}>
                                <FaQrcode /> Barcode Scanner
                            </h3>
                            {/* ✅ CLOSE BUTTON - Only when staff wants to close */}
                            <button 
                                onClick={stopScanner} 
                                style={{ 
                                    background: '#dc3545', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '6px',
                                    padding: '6px 15px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}
                                disabled={isClosing}
                            >
                                <FaTimes /> {isClosing ? 'Closing...' : 'Close Scanner'}
                            </button>
                        </div>

                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                            📷 Auto-scan active. Scan student barcodes one by one.
                            <span style={{ display: 'block', fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
                                ✅ Scanner stays open for multiple scans
                            </span>
                        </p>

                        {/* Camera View */}
                        <div id={containerId} style={{
                            width: '100%',
                            minHeight: '250px',
                            backgroundColor: '#1a1a2e',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '15px'
                        }} />

                        {/* ✅ Text Box - Auto-fills with scanned data */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Auto-filled from scan..."
                                value={barcodeInput}
                                onChange={(e) => setBarcodeInput(e.target.value)}
                                style={{ flex: 1, fontSize: '13px', backgroundColor: '#f0f4f8' }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && barcodeInput.trim()) {
                                        handleManualSubmit();
                                    }
                                }}
                            />
                            <button 
                                className="btn btn-sm btn-success"
                                onClick={handleManualSubmit}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Submit'}
                            </button>
                        </div>
                        <div style={{ fontSize: '10px', color: '#28a745', marginTop: '3px' }}>
                            ✅ Auto-fetches and auto-fills scanned barcode
                        </div>

                        {/* ✅ Scan Result Popup - Shows without closing scanner */}
                        {scanResult && (
                            <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                borderRadius: '8px',
                                backgroundColor: scanResult.success ? '#d4edda' : '#f8d7da',
                                color: scanResult.success ? '#155724' : '#721c24',
                                border: scanResult.success ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ fontSize: '14px' }}>{scanResult.message}</span>
                                {scanResult.success && scanResult.student && (
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#28a745' }}>
                                        ✅ Present
                                    </span>
                                )}
                                <button
                                    onClick={() => setScanResult(null)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#666' }}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        )}

                        {/* Attendance Stats in Scanner */}
                        <div style={{
                            marginTop: '12px',
                            padding: '10px',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-around',
                            fontSize: '12px'
                        }}>
                            <span>Total: <strong>{attendanceData.length}</strong></span>
                            <span style={{ color: '#28a745' }}>✅ Present: <strong>{attendanceData.filter(a => a.status === 'present').length}</strong></span>
                            <span style={{ color: '#dc3545' }}>❌ Absent: <strong>{attendanceData.filter(a => a.status === 'absent').length}</strong></span>
                        </div>

                        <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                            🔄 Click "Close Scanner" when done
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Entry Modal - search by name/regNo, click Present */}
            {showManualEntry && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }} onClick={() => setShowManualEntry(false)}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '25px',
                        maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ color: '#0a1e3c', margin: 0 }}><FaSearch /> Manual Entry</h3>
                            <button onClick={() => setShowManualEntry(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>
                                <FaTimes />
                            </button>
                        </div>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                            For students without a working barcode/QR code — search by name or registration number, then mark them present.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--srm-border)', borderRadius: '8px', background: 'white', marginBottom: '12px' }}>
                            <FaSearch style={{ marginLeft: '10px', color: '#999' }} />
                            <input
                                type="text"
                                className="form-control"
                                style={{ border: 'none', background: 'transparent' }}
                                value={manualSearchTerm}
                                onChange={(e) => setManualSearchTerm(e.target.value)}
                                placeholder="Type name or registration number..."
                                autoFocus
                            />
                        </div>
                        {manualSearchTerm.trim() && filteredManualStudents.length === 0 && (
                            <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>No registered student matches "{manualSearchTerm}"</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {filteredManualStudents.map(a => (
                                <div key={a.student._id} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '10px 12px', border: '1px solid #eee', borderRadius: '8px'
                                }}>
                                    <div>
                                        <strong>{a.student.name}</strong>
                                        <span style={{ color: '#666', marginLeft: '10px', fontSize: '13px' }}>{a.student.regNo || 'N/A'}</span>
                                        <span style={{ marginLeft: '10px' }} className={`badge ${a.status === 'present' ? 'badge-success' : 'badge-danger'}`}>
                                            {a.status === 'present' ? '✅ Present' : '❌ Absent'}
                                        </span>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleManualMarkPresent(a.student._id, a.student.name)}
                                        disabled={a.status === 'present'}
                                    >
                                        <FaCheckCircle /> {a.status === 'present' ? 'Already Present' : 'Present'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Students List Modal */}
            {showStudentsList && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }} onClick={() => setShowStudentsList(false)}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '25px',
                        maxWidth: '900px', width: '100%', maxHeight: '80vh', overflow: 'auto'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ color: 'var(--srm-blue)' }}><FaUsers /> Students List ({attendanceData.length})</h3>
                            <button onClick={() => setShowStudentsList(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Reg No</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendanceData.map(a => (
                                        <tr key={a.student._id}>
                                            <td>{a.student.name}</td>
                                            <td>{a.student.regNo || 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${a.status === 'present' ? 'badge-success' : 'badge-danger'}`}>
                                                    {a.status === 'present' ? '✅ Present' : '❌ Absent'}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="btn btn-sm btn-primary" onClick={() => handleViewStudentDetails(a.student)}>
                                                    <FaEye /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowStudentsList(false)}>Close</button>
                    </div>
                </div>
            )}

            {/* Student Details Modal */}
            {showStudentDetails && selectedStudentDetails && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }} onClick={() => setShowStudentDetails(false)}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '25px',
                        maxWidth: '550px', width: '100%', maxHeight: '80vh', overflow: 'auto'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ color: 'var(--srm-blue)' }}>Student Details</h3>
                            <button onClick={() => setShowStudentDetails(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                <FaTimes />
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div><strong>Name:</strong> {selectedStudentDetails.name}</div>
                            <div><strong>Email:</strong> {selectedStudentDetails.email}</div>
                            <div><strong>Reg No:</strong> {selectedStudentDetails.regNo || 'N/A'}</div>
                            <div><strong>Program:</strong> {selectedStudentDetails.program || 'N/A'}</div>
                            <div><strong>Branch:</strong> {selectedStudentDetails.branch || 'N/A'}</div>
                            <div><strong>Year:</strong> {selectedStudentDetails.year || 'N/A'}</div>
                            <div><strong>Section:</strong> {selectedStudentDetails.section || 'N/A'}</div>
                            <div><strong>Contact:</strong> {selectedStudentDetails.contact || 'N/A'}</div>
                            <div><strong>Parent Name:</strong> {selectedStudentDetails.parentName || 'N/A'}</div>
                            <div><strong>Parent Contact:</strong> {selectedStudentDetails.parentContact || 'N/A'}</div>
                            <div><strong>Parent Email:</strong> {selectedStudentDetails.parentEmail || 'N/A'}</div>
                            <div><strong>Status:</strong> 
                                <span className={`badge ${selectedStudentDetails.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                    {selectedStudentDetails.status || 'N/A'}
                                </span>
                            </div>
                        </div>
                        <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowStudentDetails(false)}>Close</button>
                    </div>
                </div>
            )}

            <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                📅 {new Date(event.date).toLocaleDateString()} at {event.time}
                {event.venue && ` 📍 ${event.venue}`}
                <span style={{ marginLeft: '10px', color: '#28a745', fontWeight: 'bold' }}>
                    📱 Click "Barcode Scanner" above to scan student IDs
                </span>
                <span style={{ marginLeft: '10px', color: '#0a1e3c', fontWeight: 'bold' }}>
                    🔄 Live-synced — updates from other staff marking this event appear automatically
                </span>
                {!isPrimaryStaff && (
                    <span style={{ marginLeft: '10px', color: '#b8860b', fontWeight: 'bold' }}>
                        🔒 Only the primary staff (event creator) can finalize and close attendance
                    </span>
                )}
            </p>

            {/* ✅ TABLE - NO SCAN BUTTONS PER ROW */}
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Reg No</th>
                            <th>Program</th>
                            <th>Branch</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.map(record => {
                            const student = record.student;
                            return (
                                <tr key={student._id}>
                                    <td><strong>{student.name}</strong></td>
                                    <td>{student.regNo || 'N/A'}</td>
                                    <td>{student.program || 'N/A'}</td>
                                    <td>{student.branch || 'N/A'}</td>
                                    <td>
                                        <span className={`badge ${record.status === 'present' ? 'badge-success' : 'badge-danger'}`}>
                                            {record.status === 'present' ? '✅ Present' : '❌ Absent'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                            <button
                                                className="btn btn-sm btn-success"
                                                onClick={async () => {
                                                    const success = await markPresent(student._id);
                                                    if (success) toast.success(`✅ ${student.name} marked present!`);
                                                }}
                                                disabled={record.status === 'present'}
                                            >
                                                Present
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger" 
                                                onClick={() => markAbsent(student._id)}
                                                disabled={record.status === 'absent'}
                                            >
                                                Absent
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-info" 
                                                onClick={() => handleViewStudentDetails(student)}
                                            >
                                                <FaEye /> View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Attendance Summary */}
            <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '10px' }}>
                <div><span style={{ color: '#666' }}>Total Students:</span> <strong style={{ fontSize: '18px' }}>{attendanceData.length}</strong></div>
                <div><span style={{ color: '#28a745' }}>✅ Present:</span> <strong style={{ fontSize: '18px', color: '#28a745' }}>{attendanceData.filter(a => a.status === 'present').length}</strong></div>
                <div><span style={{ color: '#dc3545' }}>❌ Absent:</span> <strong style={{ fontSize: '18px', color: '#dc3545' }}>{attendanceData.filter(a => a.status === 'absent').length}</strong></div>
            </div>
        </div>
    );
};

export default MarkAttendance;